import { InternalRespinSpecGroup } from '@cgs/data-access';
import { Action, Container, EmptyAction, IntervalAction } from '@cgs/wge';
import {
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  IGameStateMachineProvider,
} from '../../../reels_engine/game_components_providers/i_game_state_machine_provider';
import { AbstractGameConfig } from '../../../reels_engine/game_config/abstract_game_config';
import { SpinConfig } from '../../../reels_engine/game_config/game_config';
import { RegularSpinsSoundModel } from '../../../reels_engine/reels_sound_model';
import { GameStateMachine } from '../../../reels_engine/state_machine/game-state-machine';
import {
  T_IGameConfigProvider,
  T_IStartSlotActionProvider,
  T_IStopSlotActionProvider,
  T_RegularSpinsSoundModelComponent,
  T_WinLineActionComponent,
} from '../../../type_definitions';
import { IStartSlotActionProvider, SpinMode } from '../i_start_slot_action_provider';
import { IStopSlotActionProvider } from '../i_stop_slot_action_provider';
import { IGameConfigProvider } from '../interfaces/i_game_config_provider';
import { ISlotPrimaryActionsProvider } from '../interfaces/i_slot_primary_actions_provider';
import { RegularSpinsSoundModelComponent } from '../regular_spins_sound_model_component';
import { WinLineActionComponent } from './complex_win_line_action_providers/win_line_action_component';

export class SlotPrimaryActionsProvider implements ISlotPrimaryActionsProvider {
  private readonly _winLineComponent: WinLineActionComponent;
  private readonly _gameStateMachine: GameStateMachine<any>;
  private readonly _gameConfig: AbstractGameConfig;
  private readonly _regularSpinSoundModel: RegularSpinsSoundModel;
  private readonly _startSlotActionProvider: IStartSlotActionProvider;
  private readonly _stopSlotActionProvider: IStopSlotActionProvider;

  constructor(container: Container) {
    this._gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;
    this._gameConfig = container.resolve<IGameConfigProvider>(T_IGameConfigProvider).gameConfig;
    this._regularSpinSoundModel = container.resolve<RegularSpinsSoundModelComponent>(
      T_RegularSpinsSoundModelComponent
    ).regularSpinSoundModel;
    this._winLineComponent = container.resolve<WinLineActionComponent>(T_WinLineActionComponent);
    this._startSlotActionProvider = container.resolve(T_IStartSlotActionProvider);
    this._stopSlotActionProvider = container.resolve(T_IStopSlotActionProvider);
  }

  public getStartSlotAction(): Action {
    const respinGroup =
      this._gameStateMachine.curResponse.additionalData instanceof InternalRespinSpecGroup
        ? (this._gameStateMachine.curResponse.additionalData as InternalRespinSpecGroup)
        : null;

    return this._startSlotActionProvider.getStartSlotAction(
      respinGroup && respinGroup.respinCounter < respinGroup.groups.length
        ? SpinMode.ReSpin
        : SpinMode.Spin
    );
  }

  public getStopSlotAction(): Action {
    const respinGroup =
      this._gameStateMachine.curResponse.additionalData instanceof InternalRespinSpecGroup
        ? (this._gameStateMachine.curResponse.additionalData as InternalRespinSpecGroup)
        : null;
    return this._stopSlotActionProvider.getStopSlotAction(
      respinGroup && respinGroup.respinStarted ? SpinMode.ReSpin : SpinMode.Spin
    );
  }

  public getImmediatelyStopSlotAction(): Action {
    return new EmptyAction().withDuration(0.0);
  }

  public getWinLinesAction(): IntervalAction {
    this._winLineComponent.update(
      this._regularSpinSoundModel,
      this._gameStateMachine.curResponse.currentWinLines,
      this._gameStateMachine.curResponse.viewReels,
      this._gameConfig.regularSpinConfig as SpinConfig,
      []
    );
    return this._winLineComponent.winLineAction;
  }

  public getShortWinLinesAction(): Action {
    this._winLineComponent.update(
      this._regularSpinSoundModel,
      this._gameStateMachine.curResponse.currentWinLines,
      this._gameStateMachine.curResponse.viewReels,
      this._gameConfig.regularSpinConfig as SpinConfig,
      []
    );
    return this._winLineComponent.shortWinLineAction;
  }

  public getSpecialWinLinesAction(): IntervalAction {
    this._winLineComponent.update(
      this._regularSpinSoundModel,
      this._gameStateMachine.curResponse.currentWinLines,
      this._gameStateMachine.curResponse.viewReels,
      this._gameConfig.regularSpinConfig as SpinConfig,
      this._gameStateMachine.curResponse.currentWinPositions
    );
    return this._winLineComponent.specialLineAction;
  }

  public getRespinWinLinesAction(): Action {
    const respinGroup = this._gameStateMachine.curResponse
      .additionalData as InternalRespinSpecGroup;

    if (respinGroup && respinGroup.respinCounter < respinGroup.groups.length - 1) {
      const winLines = respinGroup.currentRound.winLines;
      const winPosition = respinGroup.currentRound.winPositions;

      if ((winLines && winLines.length > 0) || (winPosition && winPosition.length > 0)) {
        this._winLineComponent.update(
          this._regularSpinSoundModel,
          winLines,
          respinGroup.currentRound.newViewReels,
          this._gameConfig.regularSpinConfig as SpinConfig,
          winPosition
        );
        return this._winLineComponent.shortWinLineAction;
      }
    }

    return new EmptyAction().withDuration(0.0);
  }
}
